// useResolveFormSchema.js
//
// Was `utils/dynamicDataResolver.js` - resolves an entire dynamic-form
// schema's `singleSelect`/`multiSelect`/`listManager` field options from
// live RTK Query data or static reference data (referenceType-keyed).
// Consumed directly by 7 unrelated entity-form-modal features (company,
// systemFeatures, userManagement, usageCompilation, usageSources,
// billingUnits, markets), not by one feature or the shared form-component
// system - hence a global hook, not a feature or `_components/form/` file.
//
// The old file's sibling export, `useResolveFieldItems`, is dropped here -
// confirmed zero consumers anywhere in the app, including internally.

import {
  billingCycles,
  industries,
  pricingModels,
  billingSettings,
} from "../_config/billingOptions";
import { useGetUsersByOrgIdQuery } from "../_features/userManagement/api/userApiSlice";
import { useGetAllBundlesQuery } from "../_features/plans/api/bundleApiSlice";
import { useGetSystemFeaturesQuery as useGetFeaturesQuery } from "../_features/systemFeatures/api/systemFeatureApi";
import { useGetAllMarketsByOrgIdQuery } from "../_features/markets/api/marketApi";
import { useGetAllProductByOrgIdQuery } from "../_features/products/api/productApi";
import { useGetUsageCompilationsQuery } from "../_features/usageCompilation/api/usageCompilationApi";
import { useGetBillingUnitsQuery } from "../_features/billingUnits/api/billingUnitApi";
import { useGetAllUsageSourceByOrgIdQuery } from "../_features/usageSources/api/usageSourceApi";
import { useSelector } from "react-redux";
import { useMemo } from "react";

/**
 * Hook to resolve an entire form schema with dynamic data
 * @param {Array} schema - The form schema sections
 * @param {Object} filters - The filters object with properties like market, status, etc.
 * @returns {Array} - Resolved schema with items populated
 */
export function useResolveFormSchema(schema, filters = null) {
  const selectedOrg = useSelector((state) => state?.auth?.user?.selectedOrg);

  const { data: allUsers } = useGetUsersByOrgIdQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const { data: allBundles, isLoading: isBundlesLoading } =
    useGetAllBundlesQuery({});
  const { data: allSystemFeatures, isLoading: isSystemFeaturesLoading } =
    useGetFeaturesQuery({});
  const { data: allMarkets, isLoading: isMarketsLoading } =
    useGetAllMarketsByOrgIdQuery({
      status: "Active",
    });
  const { data: allMarketsCountries } =
    useGetAllMarketsByOrgIdQuery({});
  const { data: allProducts, isLoading: isProductsLoading } =
    useGetAllProductByOrgIdQuery({});
  const { data: allUsageCompilations, isLoading: isUsageCompilationsLoading } =
    useGetUsageCompilationsQuery({});

  const filteredUsers = useMemo(
    () =>
      allUsers
        ?.filter(
          (user) =>
            user?.organisations
              ?.find((org) => org?.orgId === selectedOrg)
              ?.status?.toLowerCase() === "active"
        )
        ?.map((user) => ({
          ...user,
          name: `${user?.firstName} ${user?.lastName}`,
        })),
    [allUsers, selectedOrg]
  );

  // Filter add-ons based on selected filters
  const allAddOns = useMemo(() => {
    const productsToUse = allProducts;
    const isLoadingProducts = isProductsLoading;

    if (!productsToUse || isLoadingProducts) return [];

    let filteredProducts = productsToUse.filter(
      (product) => product?.status === "Active" && product?.isAddOn
    );

    // Apply market filter if provided
    if (filters?.market && filters.market._id) {
      filteredProducts = filteredProducts.filter((product) => {
        const productMarketId = product.market?._id || product.market;
        return productMarketId === filters.market._id;
      });
    }

    return filteredProducts;
  }, [allProducts, filters?.market, isProductsLoading]);
  const { data: allBillingUnits } = useGetBillingUnitsQuery();
  const { data: allUsageSources } = useGetAllUsageSourceByOrgIdQuery();
  const { data } = useSelector((state) => state?.data);

  const resolvedSchema = useMemo(() => {
    if (!schema || !Array.isArray(schema)) {
      return [];
    }

    return schema.map((section) => {
      const resolvedSection = { ...section };

      if (section.fields && Array.isArray(section.fields)) {
        resolvedSection.fields = section.fields.map((field) => {
          const resolvedField = { ...field };

          // Resolve items for singleSelect, multiSelect, and listManager fields
          if (
            field.type === "singleSelect" ||
            field.type === "multiSelect" ||
            field.type === "listManager"
          ) {
            // If field already has items and they're not empty, keep them
            if (
              field.items &&
              Array.isArray(field.items) &&
              field.items.length > 0
            ) {
              resolvedField.items = field.items;
            } else if (
              field.dataSourceType === "External Reference" &&
              field.dynamicConfig?.referenceType
            ) {
              const { referenceType } = field.dynamicConfig;

              switch (referenceType) {
                case "industries":
                  resolvedField.items = industries;
                  // Configure industry field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  // industries (_config/billingOptions.js) items are
                  // {id, name} - not the Mongo-style `_id` every other
                  // referenceType case here defaults to. That mismatch
                  // left option.value undefined for every industry, so
                  // Select's `key={option.value}` warned about duplicate
                  // keys (all of them the same, undefined) and a selected
                  // industry could never round-trip back to itself either.
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "name";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["name"];
                  break;
                case "users":
                  resolvedField.items = filteredUsers || [];
                  // Configure user field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "_id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "name";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["name", "email"];
                  // Add user-specific render function
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => {
                      const contactNameFormat = (contactName) => {
                        const contactNameArray = contactName?.split(" ");
                        if (!contactNameArray || contactNameArray.length === 0)
                          return "";
                        if (contactNameArray.length === 1) {
                          return contactNameArray[0].slice(0, 2).toUpperCase();
                        }
                        return contactNameArray
                          .slice(0, 2)
                          .map((name) => name.charAt(0).toUpperCase())
                          .join("");
                      };

                      return (
                        <div className="company-owner-item" key={item?._id}>
                          <div className="company-owner-item-left">
                            <div className="company-owner-item-logo">
                              {contactNameFormat(item?.name)}
                            </div>
                          </div>
                          <div className="company-owner-item-right">
                            <div className="company-owner-item-name">
                              {item?.name}
                            </div>
                            <div className="company-owner-item-email">
                              {item?.email}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  break;
                case "bundles":
                  resolvedField.items = allBundles?.data || [];
                  resolvedField.isLoading = Boolean(isBundlesLoading);
                  // Configure bundle field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "name";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "name";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["name"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ?? ((item) => <p>{item?.name}</p>);
                  break;
                case "markets":
                  resolvedField.items = allMarkets || [];
                  resolvedField.isLoading = Boolean(isMarketsLoading);
                  // Configure market field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "marketName";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "marketName";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["marketName"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <p>{item?.marketName}</p>);
                  break;
                case "systemFeatures":
                  resolvedField.items = allSystemFeatures || [];
                  resolvedField.isLoading = Boolean(isSystemFeaturesLoading);
                  // Configure system features field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "_id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "featureName";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["featureName"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item.featureName}</div>);
                  break;
                case "products":
                  resolvedField.items = allProducts || [];
                  resolvedField.isLoading = Boolean(isProductsLoading);
                  // Configure products field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "_id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "productName";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["productName"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item.productName}</div>);
                  break;
                case "addOns":
                  resolvedField.items = allAddOns || [];
                  resolvedField.isLoading = Boolean(isProductsLoading);
                  // Configure add-ons field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "_id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "productName";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["productName"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item.productName}</div>);
                  break;
                case "usageCompilations":
                  resolvedField.items = allUsageCompilations || [];
                  resolvedField.isLoading = Boolean(isUsageCompilationsLoading);
                  // Configure usage compilations field properties
                  resolvedField.allowNew = resolvedField.allowNew ?? false;
                  resolvedField.isObject = resolvedField.isObject ?? true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "_id";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "title";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["title"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item.title}</div>);
                  break;
                // "roles" has no case here: it's never admin-creatable (see
                // NewFieldModal's fixed REFERENCE_TYPES list), so the only
                // field with this referenceType is the seeded User
                // Management "role" field, always type singleSelect -
                // renderInput.jsx routes it straight to RoleAsyncSelect
                // (real server-side search) before this resolver's `items`
                // would ever be read, so populating it here would just be
                // an unused, truncation-prone fetch.
                case "billingCycle":
                  resolvedField.items = billingCycles;
                  resolvedField.allowNew = false;
                  resolvedField.isObject = false;
                  break;
                case "pricingCycle":
                  resolvedField.items = billingCycles;
                  resolvedField.allowNew = false;
                  resolvedField.isObject = false;
                  break;
                case "pricingModel":
                  resolvedField.items = pricingModels;
                  resolvedField.allowNew = false;
                  resolvedField.isObject = false;
                  break;
                case "countryCodes":
                  // Get country codes from Redux state
                  resolvedField.items =
                    data?.countryCodes?.map((countryCode, idx) => ({
                      value: countryCode?.countryName,
                      id: idx,
                    })) || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "value";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "value";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["value"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.value}</div>);
                  break;
                case "currency":
                  // Get currency from Redux state
                  resolvedField.items =
                    data?.currency?.map((currency, idx) => ({
                      value: `${currency?.name} (${currency?.shortName})`,
                      label: `${currency?.name} (${currency?.shortName})`,
                      shortName: currency?.shortName,
                      name: currency?.name,
                      id: idx,
                    })) || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "value";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "label";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? [
                      "label",
                      "shortName",
                      "name",
                    ];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.label || item?.value}</div>);
                  break;
                case "marketCountries": {
                  // Map every country already claimed by ANY market to
                  // which market has it - not just a yes/no exclusion set,
                  // so a taken country can still show up (disabled) with a
                  // "already in X" label instead of just disappearing.
                  const countryToMarket = new Map();
                  allMarketsCountries?.forEach((market) => {
                    market?.countries?.forEach((c) => {
                      if (c?.country) {
                        countryToMarket.set(c.country, {
                          marketId: market?._id,
                          marketName: market?.marketName,
                        });
                      }
                    });
                  });

                  // The market currently being edited must not flag its own
                  // already-selected countries as "taken" - only some OTHER
                  // market claiming a country makes it a real conflict.
                  const currentMarketId = filters?.currentMarketId;

                  resolvedField.items =
                    data?.countryCodes?.map((countryCode, idx) => {
                      const claimedBy = countryToMarket.get(
                        countryCode?.countryName
                      );
                      const isTakenByAnotherMarket =
                        Boolean(claimedBy) &&
                        claimedBy.marketId !== currentMarketId;

                      return {
                        value: countryCode?.countryName,
                        id: idx,
                        disabled: isTakenByAnotherMarket,
                        assignedMarketName: isTakenByAnotherMarket
                          ? claimedBy.marketName
                          : null,
                      };
                    }) || [];

                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "value";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "value";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["value"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => (
                      <div>
                        {item?.value}
                        {item?.assignedMarketName && (
                          <span className="already-assigned-note">
                            {" "}
                            &mdash; already in {item.assignedMarketName}
                          </span>
                        )}
                      </div>
                    ));
                  break;
                }
                case "billingUnits":
                  resolvedField.items = allBillingUnits || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "billingUnitName";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "billingUnitName";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["billingUnitName"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.billingUnitName}</div>);
                  break;
                case "usageSources":
                  resolvedField.items = allUsageSources || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "title";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "title";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["title"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.title}</div>);
                  break;
                case "databaseSourceType":
                  resolvedField.items = billingSettings?.origins || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "value";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "label";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["value", "label"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.label}</div>);
                  break;
                case "calculationTypes":
                  resolvedField.items = billingSettings?.calculationTypes || [];
                  resolvedField.allowNew = false;
                  resolvedField.isObject = true;
                  resolvedField.valueSelector =
                    resolvedField.valueSelector ?? "value";
                  resolvedField.valueDisplay =
                    resolvedField.valueDisplay ?? "label";
                  resolvedField.searchSelector =
                    resolvedField.searchSelector ?? ["value", "label"];
                  resolvedField.renderItem =
                    resolvedField.renderItem ??
                    ((item) => <div>{item?.label}</div>);
                  break;
                default:
                  resolvedField.items = [];
              }

              // Add listManager-specific configurations
              if (field.type === "listManager") {
                // Configure listManager field properties
                resolvedField.allowNew = resolvedField.allowNew ?? false;
                resolvedField.isObject = resolvedField.isObject ?? true;
                resolvedField.valueSelector =
                  resolvedField.valueSelector ?? "_id";
                resolvedField.valueDisplay =
                  resolvedField.valueDisplay ?? "name";
                resolvedField.searchSelector = resolvedField.searchSelector ?? [
                  "name",
                ];

                // Add listManager-specific render functions if not already set
                if (!resolvedField.renderItem) {
                  const { referenceType } = field.dynamicConfig || {};
                  switch (referenceType) {
                    case "industries":
                      resolvedField.renderItem = (item) => (
                        <div>{item.name}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.name}</div>
                      );
                      break;
                    case "users":
                      resolvedField.renderItem = (item) => (
                        <div>{item.name}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.name}</div>
                      );
                      break;
                    case "bundles":
                      resolvedField.renderItem = (item) => (
                        <div>{item.name}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.name}</div>
                      );
                      break;
                    case "markets":
                      resolvedField.renderItem = (item) => (
                        <div>{item.marketName}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.marketName}</div>
                      );
                      break;
                    case "systemFeatures":
                      resolvedField.renderItem = (item) => (
                        <div>{item.featureName}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.featureName}</div>
                      );
                      break;
                    case "products":
                      resolvedField.renderItem = (item) => (
                        <div>{item.productName}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.productName}</div>
                      );
                      break;
                    case "addOns":
                      resolvedField.renderItem = (item) => (
                        <div>{item.productName}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.productName}</div>
                      );
                      break;
                    case "usageCompilations":
                      resolvedField.renderItem = (item) => (
                        <div>{item.title}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.title}</div>
                      );
                      break;
                    default:
                      resolvedField.renderItem = (item) => (
                        <div>{item.name || item.title || item.id}</div>
                      );
                      resolvedField.renderSelectedItem = (item) => (
                        <div>{item.name || item.title || item.id}</div>
                      );
                  }
                }
              }
            } else {
              resolvedField.items = [];
            }
          }

          return resolvedField;
        });
      }

      return resolvedSection;
    });
  }, [
    schema,
    filteredUsers,
    allBundles?.data,
    allMarkets,
    allMarketsCountries,
    allSystemFeatures,
    allProducts,
    allAddOns,
    allUsageCompilations,
    allBillingUnits,
    allUsageSources,
    // Remove data from dependencies and use specific properties instead
    data?.countryCodes,
    data?.currency,
    isBundlesLoading,
    isMarketsLoading,
    isSystemFeaturesLoading,
    isProductsLoading,
    isUsageCompilationsLoading,
    filters?.currentMarketId,
  ]);

  return resolvedSchema;
}
